import { useCallback, useRef, useEffect, useMemo } from 'react';

import cn from 'classnames/bind';
import { useIsRedirecting, useToggleState } from 'hooks';
import Router from 'next/router';
import PropTypes from 'prop-types';
import { Button, Col, Container, ErrorMessage, ProgressBar, Row, Text } from 'ui';
import Question from 'ui/components/tests/Question';
import ValidationFailedPopup from 'ui/components/tests/ValidationFailedPopup';
import StickyElement from 'ui/layouts/StickyElement';
import { scrollToElement, scrollBy } from 'utils/animate';
import useTest from 'utils/useTest';
import useTestResult from 'utils/useTest/useTestResult';

import s from './TestWrapper.module.scss';

const cx = cn.bind(s);
const EXTRA_TOP_OFFSET = 25;

const getQuestionOptions = ({ question, allQuestions }) => {
  if (question.type === 'order') {
    const allOptions = allQuestions.reduce((acc, q) => {
      if (Array.isArray(q.options) && q.type !== 'order') {
        return [...acc, ...q.options];
      }
      return acc;
    }, []);
    return allOptions;
  }
  return question.options;
};

const TestWrapper = ({
  children,
  className,
  schema,
  schemaName,
  token,
  preferredGender,
  preferredSkinTone,
  metaInfo,
  initOptions,
  shouldSkipResult = false,
  shouldSaveResult = false,
  shouldSkipShowResult = false,
  hasSimpleLayout = false,
  shouldScrollToStartOnRender = false,
  teamId = '',
  submit: baseSubmit,
  onSubmit,
  onChange,
  ...attrs
}) => {
  const wrapperRef = useRef();
  const questionsRefs = useRef({});
  const setQuestionRef = useCallback((element) => {
    if (!element) {
      return;
    }
    const { id } = element.dataset;
    questionsRefs.current[id] = element;
  }, []);
  const testData = useTest(schema, {
    token,
    schemaName,
    shouldSkipResult,
    shouldSaveResult,
    teamId,
    submit: baseSubmit,
    initOptions,
  });

  const {
    progress,
    isLastStep,
    questions,
    allQuestions,
    answers,
    isLoading,
    error,
    isOver,
    restart,
    setAnswer,
    nextStep,
    submit,
    stepKey,
  } = testData;
  const { result: testResult, setResult } = useTestResult(schemaName)
  const answersRef = useRef(answers);
  const resultType = useMemo(() => {
    if (schema?.meta?.result?.type) {
      return schema.meta.result.type;
    }
    return 'redirect';
  }, [schema]);
  const isRedirecting = useIsRedirecting()
  const result = useMemo(
    () =>
      resultType === 'repass' && isLastStep
        ? answers
        : testResult,
    [isLastStep, resultType, answers, testResult]
  );
  const ResultComponent = useMemo(
    () => schema?.meta?.result?.component,
    [schema]
  );
  const showResults = useMemo(() => {
    const baseCondition =
      !shouldSkipShowResult &&
      resultType !== 'redirect' &&
      ResultComponent &&
      result;
    if (resultType === 'repass') {
      return baseCondition && isLastStep;
    }
    return baseCondition && isOver;
  }, [
    isOver,
    resultType,
    shouldSkipShowResult,
    ResultComponent,
    isLastStep,
    result,
  ]);
  const {
    isActive: isValidationFailed,
    open: setIsValidationFailed,
    close: unsetIsValidationFailed,
  } = useToggleState(false);
  const showActionRow =
    (!isLastStep || !showResults || (isLastStep && resultType === 'repass')) &&
    schema.meta?.hasSubmit !== false;
  const scrollToBlockStart = useCallback(() => {
    if (wrapperRef.current) {
      scrollToElement(wrapperRef.current, {
        getExtraOffset() {
          return EXTRA_TOP_OFFSET;
        },
      });
    }
  }, []);
  useEffect(() => {
    if (shouldScrollToStartOnRender) {
      scrollToBlockStart();
    }
  }, [scrollToBlockStart, shouldScrollToStartOnRender]);
  const handleNextButtonClick = useCallback(() => {
    const isValid = nextStep();
    if (isValid) {
      scrollToBlockStart();
    } else {
      setIsValidationFailed();
    }
  }, [nextStep, scrollToBlockStart, setIsValidationFailed]);
  const setAnswerWrapper = useCallback(
    (ev) => {
      const el = questionsRefs.current[ev.name];
      const isScrollType =
        ev.questionType === 'range' || ev.questionType === 'options';
      const isAppropriateQuestion = el?.scrollHeight && isScrollType;
      if (
        isAppropriateQuestion &&
        !showResults &&
        !schema.meta?.noScrollOnAnswer &&
        ev.oldValue === undefined
      ) {
        scrollBy(el.scrollHeight);
      }
      setAnswer(ev);
    },
    [showResults, schema, setAnswer]
  );

  const dispatchDataToStore = useCallback(
    (newTestResult) => {
      setResult(newTestResult, {
        ...metaInfo,
        answers: answersRef.current,
      })
    },
    [setResult, metaInfo]
  );
  const restartWrapper = useCallback(() => {
    dispatchDataToStore();
    restart();
    scrollToBlockStart();
  }, [restart, scrollToBlockStart, dispatchDataToStore]);
  const handleSubmit = useCallback(async () => {
    if (resultType === 'repass') {
      return restart();
    }
    const testSubmitResult = await submit();
    if (testSubmitResult === 'validation') {
      setIsValidationFailed();
    } else if (typeof testSubmitResult === 'object') {
      await onSubmit?.(testSubmitResult);
      dispatchDataToStore(testSubmitResult);
    }
    if (testSubmitResult === 'skip') {
      dispatchDataToStore();
    }
    const redirectPath = (() => {
      if (resultType !== 'redirect') {
        return null;
      }
      const path = schema.meta?.result?.path || 'result'
      return typeof path === 'function' ? path() : path;
    })();
    if (
      testSubmitResult &&
      testSubmitResult !== 'validation' &&
      redirectPath &&
      !shouldSkipShowResult
    ) {
      const isAbsolute = redirectPath.startsWith('/');
      Router.replace({
        pathname: isAbsolute
          ? redirectPath
          : `${Router.asPath}/${redirectPath}`,
      });
    }
    return true;
  }, [
    shouldSkipShowResult,
    resultType,
    schema,
    submit,
    dispatchDataToStore,
    setIsValidationFailed,
    onSubmit,
    restart,
  ]);

  useEffect(() => {
    if (
      schema?.meta?.resetResultOnChange &&
      result &&
      answers !== answersRef.current
    ) {
      dispatchDataToStore();
    }
    answersRef.current = answers;
  }, [answers, result, schema, dispatchDataToStore]);
  useEffect(() => {
    onChange?.({ answers, progress });
  }, [answers, progress, onChange]);
  const isGridSizeMedium = schema?.meta?.gridSize === 'medium';
  const isStackedQuestion = (elements, index) =>
    elements[index + 1]?.isStacked ||
    (elements[index + 1] && !elements[index + 1].title);

  const mainColProps = hasSimpleLayout
    ? { xs: 12 }
    : {
      xs: 12,
      lg: {
        offset: isGridSizeMedium ? 1 : 2,
        span: isGridSizeMedium ? 10 : 8,
      },
      xl: {
        offset: 2,
        span: 8,
      },
    };
  const submitText = (() => {
    if (resultType === 'repass') {
      return 'Repass';
    }
    return schema?.meta?.submitText || 'Submit';
  })();
  return (
    <div
      className={cx(className, 'wrapper', {
        wrapperSimpleLayout: hasSimpleLayout,
      })}
      ref={wrapperRef}
      {...attrs}
    >
      <Container>
        {!schema?.meta?.hideProgress && (
          <Row className={cx('progressContainer')}>
            <Col md={{ offset: 2, span: 8 }} lg={{ offset: 3, span: 6 }}>
              <StickyElement>
                <ProgressBar progress={progress} color={metaInfo?.color} />
              </StickyElement>
            </Col>
          </Row>
        )}
        {schema.title && (
          <Row>
            <Col>
              <Text as="h2"
                className={cx(
                  'testTitle',
                )}
              >
                {schema.title}
              </Text>
            </Col>
          </Row>
        )}
        <Row className={cx('questionsRow')}>
          <Col {...mainColProps}>
            {questions.map((question, index, elements) => (
              <div
                key={question.id}
                data-testid="question"
                data-testtype={question.type || 'range'}
                ref={setQuestionRef}
                data-id={question.id}
                className={cx('questionWrapper', {
                  stakedQuestionWrapper:
                    isStackedQuestion(elements, index) ||
                    question.type === 'text',
                  lastQuestionWrapper: question?.isLastQuestion,
                })}
              >
                {!!question.sectionTitle && (
                  <Text as="h2"
                    className={cx(
                      'sectionTitle'
                    )}
                  >
                    {question.sectionTitle}
                  </Text>
                )}
                {!!question.sectionTitle && (
                  <Text
                    className={cx(
                      'sectionDescription'
                    )}
                  >
                    {question.sectionDescription}
                  </Text>
                )}
                <Question
                  name={question.id}
                  value={answers[question.id]}
                  header={question.header}
                  question={question.title}
                  description={question.description}
                  descriptionText={question.descriptionText}
                  optional={question.optional}
                  CustomComponent={question.component}
                  type={question.type || 'range'}
                  options={getQuestionOptions({
                    question,
                    allQuestions,
                    answers,
                  })}
                  reverse={
                    question.startWithPositive ||
                    schema?.questionOptions?.startWithPositive
                  }
                  singleType={
                    question.only || schema?.questionOptions?.onlyType
                  }
                  agreeLabel={
                    question.agreeLabel ||
                    schema?.questionOptions?.variants?.[stepKey]?.[0] ||
                    undefined
                  }
                  disagreeLabel={
                    question.disagreeLabel ||
                    schema?.questionOptions?.variants?.[stepKey]?.[1] ||
                    undefined
                  }
                  isStacked={question.isStacked || question.stackTitle}
                  stackTitle={question.stackTitle}
                  isLastStackedQuestion={question?.isLastQuestion}
                  hasBorder={
                    !elements[index + 1]?.isStacked && !question?.nonBordered
                  }
                  answerExtraProps={question.attrs}
                  schema={question.schema}
                  content={question.content}
                  color={metaInfo?.color}
                  onChange={setAnswerWrapper}
                />
              </div>
            ))}
          </Col>
        </Row>

        {error && (
          <div className={cx('errorRow')}>
            <ErrorMessage text={error} />
          </div>
        )}
        {showResults && (
          <ResultComponent
            preferredGender={preferredGender}
            preferredSkinTone={preferredSkinTone}
            result={result}
            restart={restartWrapper}
          />
        )}
        {showActionRow && (
          <Row className={cx('actionRow')}>
            <Col>
              {!isLastStep && (
                <Button hasIcon data-testid="test-next" onClick={handleNextButtonClick}>
                  Next
                </Button>
              )}
              {isLastStep && (
                <Button
                  hasIcon={resultType === 'redirect'}
                  disabled={isLoading || isRedirecting}
                  data-testid="test-submit"
                  onClick={handleSubmit}
                >
                  {submitText}
                </Button>
              )}
            </Col>
          </Row>
        )}
      </Container>
      <ValidationFailedPopup
        isOpen={isValidationFailed}
        title={schema.meta?.validations?.[stepKey]}
        onClose={unsetIsValidationFailed}
      />
    </div>
  );
};

TestWrapper.propTypes = {
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  schema: PropTypes.object.isRequired,
  schemaName: PropTypes.string.isRequired,
  onSubmit: PropTypes.func,
  shouldSkipResult: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
};

export default TestWrapper;
